(function () {
  'use strict';

  function parseVariants(form) {
    try {
      return JSON.parse(form.querySelector('[data-jeff-featured-variants]').textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  function parseColorImages(form) {
    try {
      const script = form.querySelector('[data-jeff-featured-color-images]');
      return script ? JSON.parse(script.textContent || '{}') : {};
    } catch (error) {
      return {};
    }
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + Math.round(cents / 100).toLocaleString('es-CO');
  }

  function getSelections(form) {
    const selections = [];
    form.querySelectorAll('[data-option-position]').forEach((group) => {
      const index = Number(group.dataset.optionPosition);
      const select = group.querySelector('select[data-jeff-featured-option]');
      const selected = group.querySelector('[data-jeff-featured-option].is-selected');
      if (Number.isFinite(index)) selections[index] = select ? select.value : selected?.dataset.optionValue;
    });
    return selections;
  }

  function findVariant(variants, selections) {
    if (!selections.length) return variants.find((variant) => variant.available) || variants[0] || null;
    return variants.find((variant) => variant.available && variant.options.every((value, index) => selections[index] === value)) || null;
  }

  function updateOptionAvailability(form, variants, selections) {
    const optionsContainer = form.querySelector('[data-jeff-featured-options]');
    const colorIndexVal = optionsContainer?.dataset.colorIndex ?? form.dataset.colorIndex;
    const colorIndex = colorIndexVal !== undefined && colorIndexVal !== '' ? Number(colorIndexVal) : -1;

    form.querySelectorAll('[data-jeff-featured-option]').forEach((control) => {
      if (control.matches('select')) return;
      const optionIndex = Number(control.dataset.optionIndex);
      const value = control.dataset.optionValue;

      // Color swatches remain clickable so merchant visitors can always preview images
      if (optionIndex === colorIndex) {
        const anyAvailableInColor = variants.some((v) => v.available && v.options[optionIndex] === value);
        control.classList.toggle('is-unavailable', !anyAvailableInColor);
        control.disabled = false;
        control.setAttribute('aria-disabled', String(!anyAvailableInColor));
        return;
      }

      // Sizes and other options reflect availability for the current color
      const possible = variants.some((variant) => {
        if (!variant.available || variant.options[optionIndex] !== value) return false;
        return variant.options.every((candidate, index) => index === optionIndex || !selections[index] || candidate === selections[index]);
      });
      control.disabled = !possible;
      control.setAttribute('aria-disabled', String(!possible));
      control.classList.toggle('is-unavailable', !possible);
    });
  }

  function updateFeaturedImage(form, variant, selections) {
    const section = form.closest('.jeff-featured-product') || document.querySelector('.jeff-featured-product');
    if (!section) return;
    const media = section.querySelector('[data-jeff-featured-media], .jeff-featured-product__media');
    if (!media) return;
    const img = media.querySelector('img');
    if (!img) return;

    let targetSrc = '';
    let targetSrcset = '';
    let targetAlt = '';

    const optionsContainer = form.querySelector('[data-jeff-featured-options]');
    const colorIndexVal = optionsContainer?.dataset.colorIndex ?? form.dataset.colorIndex;

    if (colorIndexVal !== undefined && colorIndexVal !== '' && selections) {
      const colorIndex = Number(colorIndexVal);
      const selectedColor = selections[colorIndex];
      if (selectedColor) {
        // 1. Look for matching swatch button dataset
        const swatches = form.querySelectorAll(`[data-jeff-featured-option][data-option-index="${colorIndex}"]`);
        for (const swatch of swatches) {
          if (swatch.dataset.optionValue === selectedColor) {
            if (swatch.dataset.imageSrc) {
              targetSrc = swatch.dataset.imageSrc;
              targetSrcset = swatch.dataset.imageSrcset || '';
              targetAlt = swatch.dataset.imageAlt || '';
            }
            break;
          }
        }

        // 2. Fallback to color images JSON map
        if (!targetSrc) {
          const colorImages = parseColorImages(form);
          const colorData = colorImages[selectedColor];
          if (colorData && colorData.src) {
            targetSrc = colorData.src;
            targetSrcset = colorData.srcset || '';
            targetAlt = colorData.alt || '';
          }
        }
      }
    }

    // 3. Fallback to resolved variant image
    if (!targetSrc && variant) {
      const varImg = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      if (varImg) {
        targetSrc = varImg;
        targetAlt = variant.featured_image?.alt || variant.title || '';
      }
    }

    if (!targetSrc) return;

    const currentSrc = img.dataset.currentSrc || '';
    if (currentSrc === targetSrc) return;

    img.dataset.pendingSrc = targetSrc;

    const preloader = new Image();
    if (targetSrcset) preloader.srcset = targetSrcset;
    preloader.src = targetSrc;

    const applyImage = () => {
      if (img.dataset.pendingSrc !== targetSrc) return;
      img.dataset.currentSrc = targetSrc;

      const sources = media.querySelectorAll('picture source');
      sources.forEach((source) => {
        if (targetSrcset) source.srcset = targetSrcset;
        else source.srcset = targetSrc;
      });

      if (targetSrcset) {
        img.srcset = targetSrcset;
      } else {
        img.removeAttribute('srcset');
      }
      img.src = targetSrc;
      if (targetAlt) {
        img.alt = targetAlt;
      }
      img.classList.remove('is-switching');
    };

    if (preloader.complete) {
      applyImage();
    } else {
      img.classList.add('is-switching');
      preloader.onload = applyImage;
      preloader.onerror = applyImage;
    }
  }

  function syncForm(form) {
    const variants = parseVariants(form);
    const selections = getSelections(form);
    let variant = findVariant(variants, selections);

    // Auto-select first available size in selected color if combination was unavailable
    const optionsContainer = form.querySelector('[data-jeff-featured-options]');
    const colorIndexVal = optionsContainer?.dataset.colorIndex ?? form.dataset.colorIndex;
    if (!variant && colorIndexVal !== undefined && colorIndexVal !== '') {
      const colorIndex = Number(colorIndexVal);
      const selectedColor = selections[colorIndex];
      if (selectedColor) {
        const fallbackVariant = variants.find((v) => v.available && v.options[colorIndex] === selectedColor);
        if (fallbackVariant) {
          fallbackVariant.options.forEach((val, idx) => {
            if (idx !== colorIndex) {
              selections[idx] = val;
              const ctrl = form.querySelector(`[data-jeff-featured-option][data-option-index="${idx}"][data-option-value="${val}"]`);
              if (ctrl) {
                form.querySelectorAll(`[data-jeff-featured-option][data-option-index="${idx}"]`).forEach((btn) => btn.classList.remove('is-selected'));
                ctrl.classList.add('is-selected');
              }
            }
          });
          variant = fallbackVariant;
        }
      }
    }

    const hiddenId = form.querySelector('[data-jeff-featured-variant]');
    const submit = form.querySelector('[data-jeff-featured-submit]');
    const feedback = form.querySelector('[data-jeff-featured-feedback]');
    const section = form.closest('.jeff-featured-product') || document.querySelector('.jeff-featured-product');

    updateOptionAvailability(form, variants, selections);

    form.querySelectorAll('[data-jeff-featured-option]').forEach((control) => {
      if (control.matches('select')) return;
      const active = control.dataset.optionValue === selections[Number(control.dataset.optionIndex)];
      control.classList.toggle('is-selected', active);
      control.setAttribute('aria-checked', String(active));
    });

    if (variant && hiddenId) hiddenId.value = variant.id;
    if (submit) {
      submit.disabled = !variant;
      submit.removeAttribute('aria-busy');
    }
    if (feedback && !variant) feedback.textContent = 'Selecciona una combinación disponible.';
    if (feedback && variant) feedback.textContent = '';

    // Synchronize price
    const priceEl = section?.querySelector('[data-jeff-featured-price]');
    const compareEl = section?.querySelector('[data-jeff-featured-compare-price]');
    if (variant && priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    // Synchronize detail link with variant ID
    const detailsLink = section?.querySelector('.jeff-featured-product__link');
    if (detailsLink && variant) {
      const base = detailsLink.getAttribute('href').split('?')[0];
      detailsLink.setAttribute('href', `${base}?variant=${variant.id}`);
    }

    // Synchronize main image with selected color
    updateFeaturedImage(form, variant, selections);

    return variant;
  }

  function initialise(form) {
    const optionsContainer = form.querySelector('[data-jeff-featured-options]');
    const initialRaw = optionsContainer?.dataset.initialOptions || form.dataset.initialOptions || '';
    const initial = initialRaw.split('||');

    if (initial.length && initial[0]) {
      form.querySelectorAll('[data-jeff-featured-option]').forEach((control) => {
        const index = Number(control.dataset.optionIndex);
        if (!initial[index]) return;
        if (control.matches('select')) control.value = initial[index];
        else {
          control.classList.toggle('is-selected', control.dataset.optionValue === initial[index]);
        }
      });
    }

    const section = form.closest('.jeff-featured-product') || document.querySelector('.jeff-featured-product');
    const media = section?.querySelector('[data-jeff-featured-media], .jeff-featured-product__media');
    const img = media?.querySelector('img');
    if (img) {
      const activeSwatch = form.querySelector('[data-jeff-featured-swatch].is-selected[data-image-src]');
      if (activeSwatch?.dataset?.imageSrc) {
        img.dataset.currentSrc = activeSwatch.dataset.imageSrc;
      } else if (img.currentSrc || img.src) {
        img.dataset.currentSrc = img.currentSrc || img.src;
      }
    }

    syncForm(form);
  }

  async function addToCart(form, event) {
    event.preventDefault();
    const variant = syncForm(form);
    const feedback = form.querySelector('[data-jeff-featured-feedback]');
    const submit = form.querySelector('[data-jeff-featured-submit]');
    if (!variant || !variant.id) {
      if (feedback) feedback.textContent = 'Selecciona una combinación disponible.';
      return;
    }
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    if (feedback) feedback.textContent = 'Añadiendo…';
    try {
      const formData = new FormData(form);
      const drawer = document.querySelector('cart-drawer');
      drawer?.setActiveElement?.(document.activeElement);
      if (drawer?.getSectionsToRender) {
        formData.append('sections', drawer.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo añadir el producto.');
      data.id = variant.id;
      if (drawer && window.jeffRenderCartDrawer) await window.jeffRenderCartDrawer(drawer, data);
      else window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      if (feedback) feedback.textContent = '';
    } catch (error) {
      if (feedback) feedback.textContent = error.message || 'No se pudo añadir el producto.';
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  }

  function initSection(section) {
    section.querySelectorAll('.jeff-featured-product__form').forEach((form) => {
      initialise(form);
      form.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const control = event.target.closest('[data-jeff-featured-option]');
        if (!control || control.matches('select') || control.disabled) return;
        form.querySelectorAll(`[data-jeff-featured-option][data-option-index="${control.dataset.optionIndex}"]`).forEach((option) => option.classList.remove('is-selected'));
        control.classList.add('is-selected');
        syncForm(form);
      });
      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-jeff-featured-option]')) syncForm(form);
      });
      form.addEventListener('submit', (event) => addToCart(form, event));
    });
  }

  function setup() {
    document.querySelectorAll('.jeff-featured-product').forEach((section) => {
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('jeff-featured-product') || event.target.querySelector('.jeff-featured-product')) {
      const section = event.target.classList.contains('jeff-featured-product') ? event.target : event.target.querySelector('.jeff-featured-product');
      if (section) initSection(section);
    }
  });
})();
